import React, { useState } from "react";
import { Form } from "react-bootstrap";
import { withTranslation } from "react-i18next";
import { Months, startYear } from "../../../core/constants/common";
import { computeAnomalyPeriodRange } from "./anomalyDateUtils";
import "./AnomalyPeriodSelector.scss";

const currentYear = new Date().getFullYear();

const buildYearOptions = () => {
  const options = [];
  for (let y = currentYear; y >= startYear; y--) {
    options.push(<option value={y} key={y}>{y}</option>);
  }
  return options;
};

const QUARTER_OPTIONS = [
  { value: "01", label: "Q1" },
  { value: "04", label: "Q2" },
  { value: "07", label: "Q3" },
  { value: "10", label: "Q4" },
];

/**
 * AnomalyPeriodSelector — fully uncontrolled.
 *
 * Renders frequency radio buttons + period dropdowns and calls
 * onChange(periodFrom: Date, periodTo: Date) whenever any selection changes.
 *
 * Props:
 *   defaultFrequency  - initial frequency ('ANNUAL' | 'QUARTERLY' | 'MONTHLY' | 'CUSTOM')
 *   defaultYear       - initial year
 *   defaultMonth      - initial month "01"–"12"
 *   defaultToYear     - initial "to" year (CUSTOM)
 *   defaultToMonth    - initial "to" month (CUSTOM)
 *   onChange          - (periodFrom: Date, periodTo: Date) => void
 */
const AnomalyPeriodSelector = ({
  t,
  defaultFrequency = "ANNUAL",
  defaultYear = currentYear,
  defaultMonth = "01",
  defaultToYear = currentYear,
  defaultToMonth = "01",
  onChange,
}) => {
  const months = Months({ t });
  const yearOptions = buildYearOptions();

  const [frequency, setFrequency] = useState(defaultFrequency);
  const [year, setYear] = useState(defaultYear);
  const [month, setMonth] = useState(defaultMonth);
  const [toYear, setToYear] = useState(defaultToYear);
  const [toMonth, setToMonth] = useState(defaultToMonth);

  const notify = (freq, y, m, tY, tM) => {
    const { periodFrom, periodTo } = computeAnomalyPeriodRange(freq, y, m, tY, tM);
    onChange(periodFrom, periodTo);
  };

  const handleFrequencyChange = (freq) => {
    setFrequency(freq);
    const newMonth = freq === "QUARTERLY" ? "01" : month;
    if (freq === "QUARTERLY") setMonth("01");
    notify(freq, year, newMonth, toYear, toMonth);
  };

  const handleYearChange = (y) => {
    const val = Number(y);
    setYear(val);
    notify(frequency, val, month, toYear, toMonth);
  };

  const handleMonthChange = (m) => {
    setMonth(m);
    notify(frequency, year, m, toYear, toMonth);
  };

  const handleToYearChange = (y) => {
    const val = Number(y);
    setToYear(val);
    notify(frequency, year, month, val, toMonth);
  };

  const handleToMonthChange = (m) => {
    setToMonth(m);
    notify(frequency, year, month, toYear, m);
  };

  return (
    <div className="anomaly-period-selector" data-testid="anomaly-period-selector">
      <div className="anomaly-period-selector__radios">
        <Form.Check
          inline
          type="radio"
          id="anomalyFreqYearly"
          label={t("Yearly.label")}
          value="ANNUAL"
          checked={frequency === "ANNUAL"}
          onChange={() => handleFrequencyChange("ANNUAL")}
        />
        <Form.Check
          inline
          type="radio"
          id="anomalyFreqQuarterly"
          label={t("Quarterly.label")}
          value="QUARTERLY"
          checked={frequency === "QUARTERLY"}
          onChange={() => handleFrequencyChange("QUARTERLY")}
        />
        <Form.Check
          inline
          type="radio"
          id="anomalyFreqMonthly"
          label={t("Monthly.label")}
          value="MONTHLY"
          checked={frequency === "MONTHLY"}
          onChange={() => handleFrequencyChange("MONTHLY")}
        />
        <Form.Check
          inline
          type="radio"
          id="anomalyFreqCustom"
          label={t("Custom.label")}
          value="CUSTOM"
          checked={frequency === "CUSTOM"}
          onChange={() => handleFrequencyChange("CUSTOM")}
        />
      </div>

      <div className="anomaly-period-selector__pickers">
        {frequency === "ANNUAL" && (
          <select
            className="form-select anomaly-period-selector__select"
            value={year}
            onChange={(e) => handleYearChange(e.target.value)}
          >
            {yearOptions}
          </select>
        )}

        {frequency === "QUARTERLY" && (
          <div className="anomaly-period-selector__row">
            <select
              className="form-select anomaly-period-selector__select"
              value={year}
              onChange={(e) => handleYearChange(e.target.value)}
            >
              {yearOptions}
            </select>
            <select
              className="form-select anomaly-period-selector__select"
              value={month}
              onChange={(e) => handleMonthChange(e.target.value)}
            >
              {QUARTER_OPTIONS.map((q) => (
                <option value={q.value} key={q.value}>{q.label}</option>
              ))}
            </select>
          </div>
        )}

        {frequency === "MONTHLY" && (
          <div className="anomaly-period-selector__row">
            <select
              className="form-select anomaly-period-selector__select"
              value={year}
              onChange={(e) => handleYearChange(e.target.value)}
            >
              {yearOptions}
            </select>
            <select
              className="form-select anomaly-period-selector__select"
              value={month}
              onChange={(e) => handleMonthChange(e.target.value)}
            >
              {months.map((m) => (
                <option value={m.value} key={m.value}>{m.label}</option>
              ))}
            </select>
          </div>
        )}

        {frequency === "CUSTOM" && (
          <div className="anomaly-period-selector__custom-range">
            <div className="anomaly-period-selector__custom-group">
              <span className="anomaly-period-selector__range-label">
                {t("AnomalyPeriodSelector.from.label")}
              </span>
              <select
                className="form-select anomaly-period-selector__select"
                value={year}
                onChange={(e) => handleYearChange(e.target.value)}
              >
                {yearOptions}
              </select>
              <select
                className="form-select anomaly-period-selector__select"
                value={month}
                onChange={(e) => handleMonthChange(e.target.value)}
              >
                {months.map((m) => (
                  <option value={m.value} key={m.value}>{m.label}</option>
                ))}
              </select>
            </div>
            <span className="period-sep">→</span>
            <div className="anomaly-period-selector__custom-group">
              <span className="anomaly-period-selector__range-label">
                {t("AnomalyPeriodSelector.to.label")}
              </span>
              <select
                className="form-select anomaly-period-selector__select"
                value={toYear}
                onChange={(e) => handleToYearChange(e.target.value)}
              >
                {yearOptions}
              </select>
              <select
                className="form-select anomaly-period-selector__select"
                value={toMonth}
                onChange={(e) => handleToMonthChange(e.target.value)}
              >
                {months.map((m) => (
                  <option value={m.value} key={m.value}>{m.label}</option>
                ))}
              </select>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default withTranslation()(AnomalyPeriodSelector);
